// AllPatientsInfoCard.jsx
import PropTypes from "prop-types";
import { Card } from "antd";
import dayjs from "dayjs";
import "./Styles.css";

function calcAge(birthday) {
  // Age en annees depuis la date de naissance
  if (!birthday) return null;
  const b = dayjs(birthday);
  if (!b.isValid()) return null;
  return dayjs().diff(b, "year");
}

// BMI robuste : si height > 3 on suppose cm, sinon m
function calcBMI(weightKg, height) {
  const w = Number(weightKg);
  const h = Number(height);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;

  const hM = h > 3 ? h / 100 : h; // cm -> m si besoin
  const bmi = w / (hM * hM);
  return Number.isFinite(bmi) ? bmi : null;
}

function avg(arr) {
  // Moyenne simple d'un tableau numerique
  if (!arr.length) return null;
  const s = arr.reduce((a, b) => a + b, 0);
  return s / arr.length;
}

function minMax(arr) {
  if (!arr.length) return { min: null, max: null };
  return { min: Math.min(...arr), max: Math.max(...arr) };
}

/**
 * Compteurs T0/T1 : on essaie plusieurs noms de champs possibles.
 * Quand tu me donnes le modèle exact, on remplace ça par le vrai mapping.
 */
function hasT0(p) {
  return Boolean(
    p?.t0 ||
      p?.T0 ||
      p?.evalT0 ||
      p?.evaluationT0 ||
      p?.evaluationT0Date ||
      p?.t0Date ||
      p?.t0Completed ||
      p?.hasT0
  );
}
function hasT1(p) {
  return Boolean(
    p?.t1 ||
      p?.T1 ||
      p?.evalT1 ||
      p?.evaluationT1 ||
      p?.evaluationT1Date ||
      p?.t1Date ||
      p?.t1Completed ||
      p?.hasT1
  );
}

function normalizeGender(p) {
  // essaie plusieurs clés possibles
  const raw =
    p?.gender ??
    p?.sex ??
    p?.sexe ??
    p?.genre ??
    p?.Gender ??
    p?.Sex ??
    p?.patientSex ??
    p?.patientGender;

  if (raw === null || raw === undefined) return null;

  // support valeurs numériques (ex: 0/1) si votre DB le fait
  if (raw === 0 || raw === "0") return "M";
  if (raw === 1 || raw === "1") return "F";

  const g = raw.toString().trim().toLowerCase();
  if (!g) return null;

  if (["m", "male", "homme", "masculin", "h"].includes(g)) return "M";
  if (["f", "female", "femme", "féminin", "feminin"].includes(g)) return "F";

  return null;
}

export default function AllPatientsInfoCard({
  patients,
  t,
  nT0,
  nT1,
}) {
  // Liste normalisee pour eviter les erreurs si la prop est absente
  const list = Array.isArray(patients) ? patients : [];

  const ages = list
    .map((p) => calcAge(p?.birthday ?? p?.dateOfBirth ?? p?.dob ?? p?.birthDate))
    .filter((x) => Number.isFinite(x) && x > 0);

  const bmis = list
    .map((p) => {
      const w = p?.weight ?? p?.weightKg ?? p?.poids ?? p?.Weight;
      const h = p?.height ?? p?.heightCm ?? p?.taille ?? p?.Height;
      return calcBMI(w, h);
    })
    .filter((x) => Number.isFinite(x));

  const avgAge = avg(ages);
  const { min: minAge, max: maxAge } = minMax(ages);

  const avgBmi = avg(bmis);
  const { min: minBmi, max: maxBmi } = minMax(bmis);

  const genders = list.map(normalizeGender);
  const men = genders.filter((g) => g === "M").length;
  const women = genders.filter((g) => g === "F").length;

  const n = list.length;

  // T0/T1 : si aucun patient ne porte d'info, on affiche N/A
  const anyT0Info = list.some((p) => hasT0(p));
  const anyT1Info = list.some((p) => hasT1(p));
  const inferredNT0 = anyT0Info ? list.filter(hasT0).length : null;
  const inferredNT1 = anyT1Info ? list.filter(hasT1).length : null;
  const t0Count = Number.isFinite(Number(nT0)) ? Number(nT0) : inferredNT0;
  const t1Count = Number.isFinite(Number(nT1)) ? Number(nT1) : inferredNT1;

  const fmt1 = (x) => (Number.isFinite(x) ? x.toFixed(1) : "N/A");
  const fmt0 = (x) => (Number.isFinite(x) ? x.toFixed(0) : "N/A");

  return (
    <Card>
      <p>
        <strong className="patient-name">
          {t ? t("Statistiques globales") : "Statistiques globales"}
        </strong>
      </p>

      <div className="info-grid">
        {/* Colonne gauche : participants / T0 / T1 */}
        <div className="info-col">
          <div className="info-row">
            <span className="info-label">Nombre de participants :</span>
            <span className="info-value">n={n}</span>
          </div>

          <div className="info-row">
            <span className="info-label">T0 :</span>
            <span className="info-value">{t0Count === null ? "N/A" : `n=${t0Count}`}</span>
          </div>

          <div className="info-row">
            <span className="info-label">T1 :</span>
            <span className="info-value">{t1Count === null ? "N/A" : `n=${t1Count}`}</span>
          </div>
        </div>

        {/* Colonne droite : âge/IMC/sexes */}
        <div className="info-col info-col--right">
          <div className="info-row">
            <span className="info-label">Moyenne d’âge :</span>
            <span className="info-value">
              {avgAge == null ? "N/A" : `${fmt0(avgAge)} ans`}{" "}
              {minAge == null ? "" : `(min ${fmt0(minAge)} - max ${fmt0(maxAge)})`}
            </span>
          </div>

          <div className="info-row">
            <span className="info-label">IMC :</span>
            <span className="info-value">
              {avgBmi == null ? "N/A" : `${fmt1(avgBmi)} kg/m²`}{" "}
              {minBmi == null ? "" : `(min ${fmt1(minBmi)} - max ${fmt1(maxBmi)})`}
            </span>
          </div>

          <div className="info-row">
            <span className="info-label">Homme :</span>
            <span className="info-value">n={men}</span>
          </div>

          <div className="info-row">
            <span className="info-label">Femme :</span>
            <span className="info-value">n={women}</span>
          </div>
        </div>
      </div>
    </Card>
  );
}

AllPatientsInfoCard.propTypes = {
  patients: PropTypes.array,
  t: PropTypes.func,
  nT0: PropTypes.number,
  nT1: PropTypes.number,
};
